import { toDictionary } from "../../util/toDictionary";
import { CabinetService } from "./";
import { ICabinet, IInteriorItem, InteriorItem, InteriorProductType, Vec2d } from "../WebApiClient";
import { IChainInfo, IInteriorProduct, IVec2d } from "../../models/IChainInfo";
import { topRight } from "../../models/IObj";
import { GableType } from "../../models/GableType";
import { replaceInArray } from "../../util/replaceInArray";
import { groupBy } from "../../util/groupBy";
import { DoorInfo } from "../../models/DoorInfo";


export interface PlaceItemInfo {
    readonly position: IVec2d;
    readonly size: IVec2d;
    readonly productId: number;
}


export class InteriorService {
    

    private readonly productDict: {
        [productId: number]: IInteriorProduct
    };

    constructor(
        private readonly cabinetService: CabinetService,
        private readonly cabinet: Readonly<ICabinet>,
        private readonly changeCabinet: (changes: Partial<ICabinet>) => void,
    ) {

        this.productDict = InteriorService.createProductDict(cabinetService.chainInfo);
    }

    getProduct(productId: number): IInteriorProduct {
        return this.productDict[productId];
    }

    setItems(items: IInteriorItem[]) {
        this.changeCabinet({ interiorItems: items.map(i => new InteriorItem(i)) });
    }


    setInteriorMaterialId(id: number | null) {
        const changes: Partial<ICabinet> = {};
        changes.interiorMaterialId = id;
        const newItems = this.cabinet.interiorItems.map(item => {
            //replace the material on the item if the material is valid for the item
            //return the item unchanged otherwise
            const product = this.getProduct(item.productId);
            if (!product)
                return item;
            const productMaterial = product.materials.find(mat => mat.interiorMaterialId === id);
            if (!productMaterial)
                return item;

            return new InteriorItem( {
                ...item,
                interiorMaterialId: id
            });
        })
        changes.interiorItems = newItems;
        this.changeCabinet(changes);
    }

    setItemMaterial(itemIndex: number, materialId: number | null): void {

        const newItems = replaceInArray(this.cabinet.interiorItems,
            i => i.index === itemIndex,
            oldItem => new InteriorItem({
                ...oldItem,
                interiorMaterialId: materialId
            })
        );
        if (newItems) {
            this.setItems(newItems);
        }
    }

    getPulloutViolations(): IInteriorItem[] {
        const doorInfos = this.cabinetService.getDoorInfos();
        const doorsByRail = groupBy(doorInfos, di => di.railNumber);

        const sightRect = this.cabinetService.getSightRect();
        if (!sightRect)
            return [];
        const sightLeftX = sightRect.position.x;
        const sightRightX = sightRect.position.x + sightRect.size.x

        const pulloutItems = this.cabinet
            .interiorItems
            .filter(item => this.getProduct(item.productId).isPullout);

        const violatingItems = pulloutItems
            .filter(item => !this.clearsAllDoors(item, doorsByRail, sightLeftX, sightRightX))
            ;
        return violatingItems;
    }

    getExceededBounds(): IInteriorItem[] {
        return this.cabinet.interiorItems
            .filter(i => this.isExceedingBounds(i));
    }

    isExceedingBounds(item: IInteriorItem): boolean {
        const interiorLimits = this.interiorLimits();
        if (item.position.x < interiorLimits.minX)
            return true;
        if (item.position.x + item.size.x > interiorLimits.maxX)
            return true;
        return false;
    }

    clearsAllDoors(item: IInteriorItem, doorsByRail: DoorInfo[][], sightLeftX: number, sightRightX: number): boolean {
        return doorsByRail.every(rail => this.clearsRail(item, rail, sightLeftX, sightRightX));
    }

    private clearsRail(item: IInteriorItem, doorGroup: DoorInfo[], sightLeftX: number, sightRightX: number) {

        const extraClearance = 1;

        //start by moving all doors all the way to the right.
        //see if the item can fit through the opening. If not,
        //move the first door on the rail to the left, and try again.
        for (let i = 0; i <= doorGroup.length; i++) {

            const leftDoors = doorGroup.filter((_, n) => n < i);
            const openingStartX = sightLeftX + this.totalSize(leftDoors);

            const rightDoors = doorGroup.filter((_, n) => n >= i);
            const openingEndX = sightRightX - this.totalSize(rightDoors);

            const fitsLeft = item.position.x + extraClearance >= openingStartX;
            const fitsRight = item.position.x + item.size.x - extraClearance <= openingEndX;

            if (fitsLeft && fitsRight) {
                return true;
            }
        }
        return false;
    }

    private totalSize(doors: DoorInfo[]) {
        return doors.map(d => d.size.x).reduce((sum, doorSize) => sum + doorSize, 0);
    }

    getWallMountViolations(): IInteriorItem[] {
        return this.cabinet.interiorItems
            .filter(i => this.isMountedOnWall(i))
    }

    isMountedOnWall(item: IInteriorItem): boolean {
        const product = this.getProduct(item.productId);
        const productType = product.productType;
        const gableInfo = this.cabinetService.getGableInfo();

        const checkLeft = (
            productType === InteriorProductType.GableShelf
            || productType === InteriorProductType.ShelfOrDrawer
            || productType === InteriorProductType.LeftMount
            || (productType === InteriorProductType.LeftOrRightMount && !item.isFlipped)
        ) && gableInfo.left.type !== GableType.Gable;
        const checkRight = (
            productType === InteriorProductType.GableShelf
            || productType === InteriorProductType.ShelfOrDrawer
            || productType === InteriorProductType.RightMount
            || (productType === InteriorProductType.LeftOrRightMount && item.isFlipped)
        ) && gableInfo.right.type !== GableType.Gable;
        if (!checkLeft && !checkRight)
            return false;
        const limits = this.interiorLimits();

        if (checkLeft) {
            if (limits.minX + 1 >= item.position.x)
                return true;
        }
        if (checkRight) {
            if (limits.maxX - 1 <= item.position.x + item.size.x)
                return true;
        }
        return false;
    }

    moveGable(index: number, dist: number) {
        const max = this.maxGableMovement(index);
        const clampedDist = dist < 0
            ? Math.max(-max.left, dist)
            : Math.min(max.right, dist);
        if (clampedDist === 0)
            return;
        const oldCabinet = this.cabinet;
        const allItems = oldCabinet.interiorItems;
        const oldItem = allItems.find(i => i.index === index);
        if (!oldItem)
            return;
        const newItem = new InteriorItem({
            ...oldItem,
            position: new Vec2d({
                x: oldItem.position.x + clampedDist,
                y: oldItem.position.y
            }),
        });

        const newItems = allItems.filter(i => i !== oldItem).concat(newItem);
        this.changeCabinet({ interiorItems: newItems });
    }

    maxGableMovement(gableIndex: number): { left: number, right: number } {
        const oldCabinet = this.cabinet;
        const allItems = oldCabinet.interiorItems;
        const oldItem = allItems.find(i => i.index === gableIndex);
        if (!oldItem)
            return {
                left: 0,
                right: 0,
            };
        const itemTopRight = topRight(oldItem);
        const gableInfo = this.cabinetService.getGableInfo();
        const minX = gableInfo.left.type === GableType.Gable
            ? gableInfo.left.position.x + gableInfo.left.size.x
            : 0;
        const maxX = gableInfo.right.type === GableType.Gable
            ? gableInfo.right.position.x
            : oldCabinet.width ?? 0;

        return {
            left: Math.max(0, oldItem.position.x - minX),
            right: Math.max(0, maxX - itemTopRight.x)
        };
    }

    interiorLimits(): { minX: number, maxX: number } {
        const oldCabinet = this.cabinet;
        const gableInfo = this.cabinetService.getGableInfo();
        const minX = gableInfo.left.type === GableType.Gable
            ? gableInfo.left.position.x + gableInfo.left.size.x
            : 0;
        const maxX = gableInfo.right.type === GableType.Gable
            ? gableInfo.right.position.x
            : oldCabinet.width ?? 0;
        return { minX, maxX };
    }

    deleteItem(index: number) {

        const oldCabinet = this.cabinet;
        const indexesToRemove = InteriorService.getDescendentIndexes(index, oldCabinet.interiorItems);
        indexesToRemove.add(index);
        const newItems = oldCabinet.interiorItems
            .filter(item => !indexesToRemove.has(item.index));
        this.changeCabinet({ interiorItems: newItems });
    }

    private static createProductDict(chainInfo: IChainInfo): { [productId: number]: IInteriorProduct } {
        const productInfos = chainInfo.productLines.flatMap(pl =>
            pl.interiorProducts.flatMap(pg =>
                pg.interiorProductSubGroups.flatMap(psg => psg.products)
            )
        );
        return toDictionary(productInfos, product => product.id);
    }

    static getDescendentIndexes(parentIndex: number, items: IInteriorItem[]): Set<number> {
        const parentIndexes = new Set([parentIndex]);
        const result = new Set<number>();
        let foundAny = true;
        while (foundAny) {
            foundAny = false;
            for (let potentialChild of items) {
                for (let childParentIndex of potentialChild.parentIndexes) {
                    if (parentIndexes.has(childParentIndex)) {
                        if (!result.has(potentialChild.index)) {
                            foundAny = true;
                            result.add(potentialChild.index);
                        }
                        break;
                    }
                }
            }
        }
        return result;
    }
}

